/**
 * CaseManageSlice Selectors
 * 
 * Memoized selectors for accessing case manage slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { CaseManageSliceState } from './state';

// Base selector to get the case manage slice
const selectCaseManageSlice = (state: RootState) => state.caseManage as unknown as CaseManageSliceState;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current case entity
 */
export const selectCurrentCase = createSelector(
  selectCaseManageSlice,
  (slice) => slice.currentCase
);

/**
 * Select whether current case exists
 */
export const selectHasCurrentCase = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage !== null
);

/**
 * Select the case ID (if exists)
 */
export const selectCaseId = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.id
);

/**
 * Select account number
 */
export const selectAccountNumber = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.accountNumber
);

// =============================================================================
// EDIT TRACKING SELECTORS
// =============================================================================

/**
 * Select whether any edits have been made
 */
export const selectCaseIsEdited = createSelector(
  selectCaseManageSlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectCaseEditedFields = createSelector(
  selectCaseManageSlice,
  (slice) => slice.editedFields
);

/**
 * Factory to create a selector for checking if a specific field is edited
 */
export const selectIsCaseFieldEdited = (fieldName: string) =>
  createSelector(selectCaseEditedFields, (fields) => fields.includes(fieldName));

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectCaseValidationErrors = createSelector(
  selectCaseManageSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasCaseValidationErrors = createSelector(
  selectCaseValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory to create a selector for a specific field's validation error
 */
export const selectCaseValidationError = (fieldName: string) =>
  createSelector(selectCaseValidationErrors, (errors) => errors[fieldName]);

// =============================================================================
// SUB-ENTITY SELECTORS
// =============================================================================

/**
 * Select clinical review details
 */
export const selectClinicalReview = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.clinicalReview
);

/**
 * Select document request details
 */
export const selectDocumentRequest = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.documentRequest
);

/**
 * Select case notes
 */
export const selectCaseNotes = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.notes ?? []
);

/**
 * Select case call logs
 */
export const selectCaseCallLogs = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.callLogs ?? []
);

/**
 * Select coding details
 */
export const selectCodingDetails = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.codingDetails
);

/**
 * Select policy links
 */
export const selectPolicyLinks = createSelector(
  selectCurrentCase,
  (caseManage) => caseManage?.policyLinks ?? []
);
